import React from 'react';
import { DataSuGo, ETICHETTE_DATA_SU, PeriodoGo } from '../../lib/gestionaleOpen';

/**
 * Periodo e data di riferimento delle pagine GO: un anno (tutto, o da un mese
 * a un altro) oppure un intervallo di date libere, e la data su cui contarli
 * (documento, registrazione, competenza).
 *
 * La stessa fattura puo' cadere in mesi diversi a seconda della data: su GO
 * quella di registrazione segue di solito di un mese la data documento, e la
 * competenza di bilancio la scrive la contabilita'. Per un taglio infrannuale
 * (es. gennaio–agosto) la scelta cambia il risultato, per questo e' in vista.
 */

const ANNO_CORRENTE = new Date().getFullYear();
const ANNI = Array.from({ length: 6 }, (_, i) => String(ANNO_CORRENTE - i));
const INTERVALLO = 'intervallo';
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

export function periodoIniziale(dataSu: DataSuGo | '' = ''): PeriodoGo {
  return { intervallo: false, anno: String(ANNO_CORRENTE), da: '', a: '', meseDa: 1, meseA: 12, dataSu };
}

interface Props {
  periodo: PeriodoGo;
  onChange: (p: PeriodoGo) => void;
  /** Date ammesse dalla vista, nell'ordine in cui mostrarle. Vuoto = nessuna scelta. */
  dateAmmesse: DataSuGo[];
  /** Mostra la voce «Tutti gli anni». */
  conTuttiGliAnni?: boolean;
  /** Etichette della tendina data, se diverse da «Per data …». */
  etichette?: Partial<Record<DataSuGo, string>>;
}

export default function FiltroPeriodoGo({ periodo, onChange, dateAmmesse, conTuttiGliAnni = false, etichette }: Props) {
  const valorePeriodo = periodo.intervallo ? INTERVALLO : periodo.anno;

  const cambiaPeriodo = (v: string) => {
    if (v === INTERVALLO) {
      // Si parte dall'anno scelto, cosi' l'intervallo e' subito valido
      const anno = periodo.anno || String(ANNO_CORRENTE);
      onChange({ ...periodo, intervallo: true, da: periodo.da || `${anno}-01-01`, a: periodo.a || `${anno}-12-31` });
    } else {
      onChange({ ...periodo, intervallo: false, anno: v });
    }
  };

  return (
    <>
      <select
        className="bop-select"
        value={valorePeriodo}
        onChange={(e) => cambiaPeriodo(e.target.value)}
        aria-label="Periodo"
      >
        {conTuttiGliAnni && <option value="">Tutti gli anni</option>}
        {ANNI.map((a) => <option key={a} value={a}>{a}</option>)}
        <option value={INTERVALLO}>Intervallo di date…</option>
      </select>

      {!periodo.intervallo && periodo.anno && (
        <span className="bop-filtro-periodo-go__intervallo">
          <select
            className="bop-select bop-filtro-periodo-go__mese"
            value={periodo.meseDa}
            onChange={(e) => { const v = Number(e.target.value); onChange({ ...periodo, meseDa: v, meseA: Math.max(v, periodo.meseA) }); }}
            aria-label="Da mese"
          >
            {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <span className="bop-filtro-periodo-go__al">a</span>
          <select
            className="bop-select bop-filtro-periodo-go__mese"
            value={periodo.meseA}
            onChange={(e) => { const v = Number(e.target.value); onChange({ ...periodo, meseA: v, meseDa: Math.min(v, periodo.meseDa) }); }}
            aria-label="A mese"
          >
            {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </span>
      )}

      {periodo.intervallo && (
        <span className="bop-filtro-periodo-go__intervallo">
          <input
            className="bop-input bop-filtro-periodo-go__data"
            type="date"
            value={periodo.da}
            max={periodo.a || undefined}
            onChange={(e) => onChange({ ...periodo, da: e.target.value })}
            aria-label="Dal"
          />
          <span className="bop-filtro-periodo-go__al">al</span>
          <input
            className="bop-input bop-filtro-periodo-go__data"
            type="date"
            value={periodo.a}
            min={periodo.da || undefined}
            onChange={(e) => onChange({ ...periodo, a: e.target.value })}
            aria-label="Al"
          />
        </span>
      )}

      {dateAmmesse.length > 1 && (
        <select
          className="bop-select"
          value={periodo.dataSu || dateAmmesse[0]}
          onChange={(e) => onChange({ ...periodo, dataSu: e.target.value as DataSuGo })}
          aria-label="Data di riferimento"
          title="Su quale data contare il periodo"
        >
          {dateAmmesse.map((d) => <option key={d} value={d}>{etichette?.[d] ?? `Per ${ETICHETTE_DATA_SU[d].toLowerCase()}`}</option>)}
        </select>
      )}
    </>
  );
}
